import { ref } from "vue";

import { type Nullable } from "shared/types";

export const THRESHOLD_VALUES = [0, 0.25, 0.5, 0.75, 1];
const OPEN_DELAY = 250;

export function useSyndicationsObserver(
  options: IntersectionObserverInit = {}
) {
  const observer = ref<Nullable<IntersectionObserver>>(null);
  const openTimestamp = ref(0);
  const isOpen = ref(false);
  const abortController = ref<Nullable<AbortController>>(null);

  const destroy = () => {
    if (abortController.value) {
      abortController.value.abort();
      abortController.value = null;
    }

    if (observer.value) {
      observer.value.disconnect();
      observer.value = null;
    }
  };

  const create = (parentElRef?: Nullable<HTMLElement>) => {
    if (!parentElRef) return;

    openTimestamp.value = Date.now();
    abortController.value = new AbortController();

    observer.value = new IntersectionObserver(
      (entries) => {
        const timeSinceOpen = Date.now() - openTimestamp.value;

        if (entries[0].intersectionRatio < 1 && timeSinceOpen > OPEN_DELAY) {
          isOpen.value = false;
          destroy();
        }
      },
      { threshold: THRESHOLD_VALUES, ...options }
    );

    observer.value.observe(parentElRef);
  };

  const toggleOpen = (
    parentElRef: Nullable<HTMLElement>,
    loadCallback?: () => Promise<void>
  ): Promise<void> => {
    isOpen.value = !isOpen.value;

    if (isOpen.value) {
      create(parentElRef);

      return Promise.resolve(loadCallback?.());
    }

    return Promise.resolve(destroy());
  };

  return {
    abortController,
    destroy,
    isOpen,
    toggleOpen,
  };
}
